import axios from 'axios';
import { useEffect, useState } from 'react';

import { logError } from '@hotjar/insights-common/lib/logger';

async function getImageAsBase64(url: string, noReject?: boolean): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .get(url, { withCredentials: true, responseType: 'blob' })
      .then((imageBlob) => {
        // We need to download the image as a blob so we can convert it to base64 to embed in the SVG.
        // If we don't convert it to an inlined image, we can't download it since the canvas will be tainted.
        const reader = new FileReader();
        reader.readAsDataURL(imageBlob.data);

        reader.onloadend = () => {
          const base64Data = reader.result as string;
          resolve(base64Data);
        };
      })
      .catch((error) => {
        if (!noReject) {
          reject(error);
        }

        logError(error);
      });
  });
}

export function useImage(
  url: string,
  noReject?: boolean,
): { size: [number, number]; encodedImage: string } {
  const [size, setImageSize] = useState<[number, number]>([0, 0]);
  const [encodedImage, setEncodedImage] = useState('');

  useEffect(() => {
    const image = new Image();

    const handleLoad = () => {
      setImageSize([image.naturalHeight, image.naturalWidth]);
    };

    image.addEventListener('load', handleLoad);

    getImageAsBase64(url, noReject)
      .then((base64Data) => {
        image.src = base64Data;
        setEncodedImage(base64Data);
      })
      .catch((error) => {
        logError(error);
      });

    return () => {
      image.removeEventListener('load', handleLoad);
    };
  }, [url, noReject]);

  return { size, encodedImage };
}
